import React, { useState, useEffect, useRef } from 'react';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
  containerRef: React.RefObject<HTMLDivElement> | null;
  threshold?: number;
  resistance?: number;
}

function getScrollableAncestor(el: Element | null, boundary: Element | null): HTMLElement | null {
  let node: Element | null = el;
  while (node) {
    if (boundary && !boundary.contains(node)) {
      break;
    }
    const htmlEl = node as HTMLElement;
    const style = getComputedStyle(node);
    const overflowY = style.overflowY;
    const scrollHeight = htmlEl.scrollHeight;
    const clientHeight = htmlEl.clientHeight;
    if ((overflowY === 'auto' || overflowY === 'scroll') && scrollHeight > clientHeight) {
      return htmlEl;
    }
    node = node.parentElement;
  }
  return boundary as HTMLElement | null;
}

const PullToRefresh = ({ onRefresh, children, containerRef, threshold = 100, resistance = 2.5 }: PullToRefreshProps) => {
  const [startY, setStartY] = useState(0);
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const cRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = cRef.current;
    if (!element) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (!element) return;

      const touchTarget = document.elementFromPoint(
        e.touches[0].clientX,
        e.touches[0].clientY
      ) as Element | null;
      const scrollEl = getScrollableAncestor(touchTarget, containerRef?.current ?? null)
        ?? containerRef?.current;
      const isAtTop = scrollEl ? scrollEl.scrollTop === 0 : true;

      if (isAtTop) {
        setStartY(e.touches[0].clientY);
        setPulling(true);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!pulling) return;

      const touchTarget = document.elementFromPoint(
        e.touches[0].clientX,
        e.touches[0].clientY
      ) as Element | null;
      const scrollEl = getScrollableAncestor(touchTarget, containerRef?.current ?? null)
        ?? containerRef?.current;

      if (!scrollEl) {
        setPulling(false);
        return;
      }

      const isAtTop = scrollEl.scrollTop === 0;
      if (!isAtTop) {
        setPulling(false);
        return;
      }

      const y = e.touches[0].clientY;
      const distance = (y - startY) / resistance;

      if (distance > 0) {
        setPullDistance(distance);
        e.preventDefault();
      }
    };

    const handleTouchEnd = async () => {
      if (!pulling) return;

      if (pullDistance >= threshold) {
        await onRefresh();
      }

      setPulling(false);
      setPullDistance(0);
    };

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [pulling, pullDistance, startY, onRefresh, resistance, threshold, containerRef]);

  return (
    <div
      ref={cRef}
      style={{ 
        position: 'relative',
        touchAction: 'pan-y pinch-zoom',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      {pulling && (
        <div
          style={{
            position: 'absolute',
            top: -20,
            left: 0,
            right: 0,
            height: `${pullDistance}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f0f0f0',
            transition: pulling ? 'none' : 'height 0.2s ease-out',
          }}
        >
          {pullDistance >= threshold ? 'Release to refresh' : 'Pull to refresh'}
        </div>
      )}
      {children}
    </div>
  );
};

export default PullToRefresh;